import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, switchMap, take, tap } from 'rxjs/operators';

import { FormHelper } from '@helpers/form.helper';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { enumEntityDataSource, enumEntityType } from '@data-layer/enums/isf';
import { DESC_ENTITY } from '@constants/desc-entity';
import { EntityModel } from '@data-layer/models/entities';
import { CodeRepository, EntitiesRepository } from '@data-layer/repositories';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { CompaniesService } from '@services/companies.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { CountryModel } from '@data-layer/models/common';
import { omit } from 'lodash-es';
import { CANADA_COUNTRY_CODE, MEXICO_COUNTRY_CODE, USA_COUNTRY_CODE } from '@constants/constants';

@Component({
  selector: 'app-entity-unify-update',
  templateUrl: './entity-unify-update.component.html',
  styleUrls: ['./entity-unify-update.component.scss'],
})
export class EntityUnifyUpdateComponent implements OnChanges, OnDestroy {
  @Input() public entity: EntityModel;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public errors: { [name: string]: IErrorForm } = {};
  @Input() public code: string = '';
  @Input() public label: string = '';
  @Input() public selectedValueControl: UntypedFormControl = new UntypedFormControl();
  @Input() public isShowCard: boolean = true;
  @Input() public isOnlyEdit: boolean = false;
  @Input() public isDesktop: boolean = false;
  @Input() public isShowIdentifier: boolean = true;
  @Input() public isShowPhone: boolean;
  @Input() public isShowEmail: boolean;
  @Input() public isWithFooter: boolean;
  @Input() public isLCLForeign: boolean;
  @Input() public isResetCounty: boolean = true;
  @Output() public formCleared: EventEmitter<void> = new EventEmitter<void>();

  public info: any = null;
  public companyName: AbstractControl;
  public enumEntityDataSource: typeof enumEntityDataSource = enumEntityDataSource;
  public isShowAddress: boolean = true;
  public shouldShowEmail: boolean = false;
  public shouldShowPhone: boolean = false;
  public shouldShowFax: boolean = false;
  public companyLoading: boolean = false;
  public defaultCountry: any = null;
  public CUSTOM_MASK: any = CUSTOM_MASK;
  public entities: null | any[] = null;
  public type: enumEntityType;
  public errorsPhone = {
    maxlength: 'FORM_ERROR.MAX_LENGTH',
    validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER',
  };
  public enumEntityModelCode = enumEntityModelCode;

  private subscription: Subscription[] = [];
  private valueCompany: null | string = null;
  private buyerEntities: enumEntityModelCode[] = [
    enumEntityModelCode.IMPORTER,
    enumEntityModelCode.CONSIGNEE,
    enumEntityModelCode.BUYER,
    enumEntityModelCode.SHIP_TO,
  ];
  private sellerEntities: enumEntityModelCode[] = [
    enumEntityModelCode.MANUFACTURER,
    enumEntityModelCode.CONSOLIDATOR,
    enumEntityModelCode.SELLER,
    enumEntityModelCode.STAFFING_LOCATION,
  ];

  constructor(
    private fh: FormHelper,
    private cd: ChangeDetectorRef,
    private entitiesRepository: EntitiesRepository,
    private companiesService: CompaniesService,
    private codeRepository: CodeRepository,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    this.init();
  }

  public get isShowState(): boolean {
    const isSeller = this.sellerEntities.includes(this.entity?.entityCode);
    const country = this.formGroup.get(['address', 'country']).value;
    return (
      ([enumEntityModelCode.MANUFACTURER, enumEntityModelCode.SELLER].includes(this.entity?.entityCode) &&
        country &&
        [CANADA_COUNTRY_CODE, USA_COUNTRY_CODE, MEXICO_COUNTRY_CODE].includes(country.code)) ||
      !isSeller
    );
  }

  public get isForeignImporter(): boolean {
    return (
      (this.formGroup.get('entityIdentifierQualifier').value === enumIdentifierQualifier.ANI &&
        this.code === enumEntityModelCode.IMPORTER) ||
      this.isLCLForeign
    );
  }

  public get isConsigneeEntity(): boolean {
    return enumEntityModelCode.CONSIGNEE === this.entity?.entityCode;
  }

  public init() {
    if (this.isOnlyEdit) {
      this.selectedValueControl.patchValue(this.entity?.id);
    }
    this.info = this.entity && DESC_ENTITY[this.entity?.entityCode];

    this.type = this.entity && this.buyerEntities.includes(this.entity?.entityCode) ? enumEntityType.buyer : enumEntityType.seller;

    if (this.type === enumEntityType.buyer) {
      const company = this.companiesService.getCompany();
      const companyCountryCode = company?.address?.country?.code;
      this.defaultCountry = companyCountryCode || 'US';
    }

    this.label = !this.label ? `INFO.${this.code?.toLocaleUpperCase()}.${this.entity?.id ? 'EDIT' : 'ADD_NEW'}` : this.label;
    this.companyName = this.formGroup.get('name');

    this.subscription.push(
      this.companyName.valueChanges
        .pipe(
          distinctUntilChanged(),
          pairwise(),
          filter(([pre, cur]) => {
            if (!pre || !cur) {
              return false;
            }
            return Math.abs(cur.length - (pre ? pre.length : 0)) === 1;
          }),
          debounceTime(600),
          distinctUntilChanged(),
          filter(([pre, cur]) => {
            return cur && cur.length >= 3;
          }),
          switchMap((value) => {
            if (this.valueCompany && value[1].indexOf(value[0]) === 0 && this.valueCompany === value[0]) {
              return of(this.entity);
            }
            this.companyLoading = true;
            const companyId: number = this.companiesService.getCompany() ? this.companiesService.getCompany().id : null;
            if (!companyId) {
              return of([]);
            }
            return this.entitiesRepository.searchByCompany(companyId, {
              name: value[1],
              type: this.entity?.entityCode === enumEntityModelCode.ABI_CLIENT ? enumEntityModelCode.BUYER : this.entity?.entityCode,
            });
          }),
          map((value) => value),
          tap((values: any[]) => {
            if (values.length === 0) {
              this.valueCompany = this.companyName.value;
            } else {
              this.valueCompany = null;
            }
          }),
        )
        .subscribe((values) => {
          this.entities = values;
          this.companyLoading = false;
          this.cd.detectChanges();
        }),
      this.formGroup.get('entityIdentifierQualifier').valueChanges.subscribe((next) => {
        if (next !== enumIdentifierQualifier.ANI && !this.formGroup.get(['address', 'country']).value) {
          this.getDefaultCountryAddress()
            .pipe(take(1))
            .subscribe((country) => {
              this.formGroup.get(['address', 'country']).setValue(country);
            });
        }
      }),
    );

    // #5636
    /*if (this.entity.entityCode === enumEntityModelCode.CONSIGNEE) {
      this.formGroup.get('address').reset();
      this.isShowAddress = false;
    }*/

    this.shouldShowEmail = this.entity?.entityCode === enumEntityModelCode.ABI_CLIENT || this.isShowEmail;
    this.shouldShowPhone =
      this.entity?.entityCode === enumEntityModelCode.ABI_CLIENT ||
      (this.entity?.entityCode === enumEntityModelCode.IMPORTER && !this.companiesService.getCompany()) ||
      this.isShowPhone;
    this.shouldShowFax = this.entity?.entityCode === enumEntityModelCode.ABI_CLIENT;

    this.subscription.push(
      this.formGroup.get(['address', 'country']).valueChanges.subscribe((value) => {
        if (!value && this.formGroup.get(['address', 'state']).value) {
          this.formGroup.get(['address', 'state']).reset();
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((e) => e.unsubscribe());
  }

  public changeAddress(item: any) {
    const valuesToPatch = omit(item, ['identifier', 'identifierQualifier']) as EntityModel;
    this.formGroup.patchValue(valuesToPatch);
    this.companyName.setValue(valuesToPatch.name);
    this.formGroup.get('entityIdentifier').setValue(item.identifier);
    this.formGroup.get('entityIdentifierQualifier').setValue(item.identifierQualifier);
  }

  public resetForm(): void {
    this.formGroup.reset({
      address: {
        ...Object.keys(this.formGroup.get('address').value).map((item) => ({ [item]: null })),
        // TODO check country reset
        country: this.isResetCounty ? null : this.formGroup.get('address')?.get('country').value || USA_COUNTRY_CODE,
      },
      entityIdentifierQualifier: this.formGroup.get('entityIdentifierQualifier').value,
    });
    this.formCleared.emit();
  }

  public getDefaultCountryAddress(): Observable<CountryModel> {
    return this.codeRepository.getCountry(this.defaultCountry);
  }
}
